import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  createProject,
  patchProject,
  markProjectSaved,
  fetchProject,
  fetchProjectByCode,
  projectHeartbeat,
} from "./projects";

const LS_KEY = "bioedil_project";
const AUTOSAVE_MS = 2500;
const HEARTBEAT_MS = 20000;

const readLocal = () => {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || "null");
  } catch {
    return null;
  }
};

const writeLocal = (p) =>
  localStorage.setItem(LS_KEY, JSON.stringify({ id: p.id, code: p.code, token: p.token }));

export const storedProject = readLocal;

// Gestisce sessione anonima, autosave e heartbeat. Non tocca il motore di calcolo.
export const useProject = ({ state, snapshot, ready, onRestore }) => {
  const [project, setProject] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [bioedilOnline, setBioedilOnline] = useState(false);
  const [unread, setUnread] = useState(0);
  const stateRef = useRef({ state, snapshot });
  const lastSavedRef = useRef(null);
  const bootRef = useRef(false);

  stateRef.current = { state, snapshot };

  // avvio: riprende dal link, altrimenti crea una sessione anonima
  useEffect(() => {
    if (!ready || bootRef.current) return;
    bootRef.current = true;
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const token = params.get("t");
      const match = window.location.pathname.match(/\/progetto\/([^/?]+)/);
      try {
        if (match && token) {
          const p = await fetchProjectByCode(decodeURIComponent(match[1]), token);
          setProject(p);
          writeLocal(p);
          if (p.state && Object.keys(p.state).length) onRestore(p.state);
          toast.success(`Progetto ${p.code} ripreso`);
          return;
        }
        const p = await createProject();
        setProject(p);
      } catch {
        toast.error("Progetto non trovato o link non valido");
        try {
          setProject(await createProject());
        } catch {
          /* configuratore comunque utilizzabile */
        }
      }
    })();
  }, [ready, onRestore]);

  // autosave con debounce: salta se lo stato non è realmente cambiato
  useEffect(() => {
    if (!project || !ready) return;
    const payload = JSON.stringify({ state, snapshot });
    if (payload === lastSavedRef.current) return;
    setDirty(true);
    const t = setTimeout(async () => {
      try {
        const res = await patchProject(project.id, project.token, {
          state: stateRef.current.state,
          snapshot: stateRef.current.snapshot,
        });
        lastSavedRef.current = JSON.stringify(stateRef.current);
        setSavedAt(res.updated_at);
        setDirty(false);
      } catch {
        /* riprova al prossimo cambiamento */
      }
    }, AUTOSAVE_MS);
    return () => clearTimeout(t);
  }, [state, snapshot, project, ready]);

  // heartbeat leggero: in pausa quando la scheda non è visibile
  useEffect(() => {
    if (!project) return;
    const beat = async () => {
      if (document.hidden) return;
      try {
        const res = await projectHeartbeat(project.id, project.token, stateRef.current.snapshot);
        setBioedilOnline(res.bioedil_online);
        setUnread(res.unread_client);
      } catch {
        /* ignora */
      }
    };
    beat();
    const iv = setInterval(beat, HEARTBEAT_MS);
    return () => clearInterval(iv);
  }, [project]);

  const save = async () => {
    if (!project) return null;
    await patchProject(project.id, project.token, {
      state: stateRef.current.state,
      snapshot: stateRef.current.snapshot,
    });
    const res = await markProjectSaved(project.id, project.token);
    lastSavedRef.current = JSON.stringify(stateRef.current);
    const next = { ...project, saved: true };
    setProject(next);
    writeLocal(next);
    setSavedAt(new Date().toISOString());
    setDirty(false);
    return res;
  };

  const rename = async (name) => {
    if (!project) return;
    setProject((p) => ({ ...p, name }));
    try {
      await patchProject(project.id, project.token, { name });
    } catch {
      toast.error("Rinomina non riuscita");
    }
  };

  const resume = async ({ id, code, token }) => {
    const p = id ? await fetchProject(id, token) : await fetchProjectByCode(code, token);
    setProject(p);
    writeLocal(p);
    if (p.state && Object.keys(p.state).length) onRestore(p.state);
    return p;
  };

  return { project, savedAt, dirty, bioedilOnline, unread, setUnread, save, rename, resume };
};
